import { useFormik } from "formik";
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { DateUtil, sampleMode } from "../../../shared";
import { addNewLabRequestSample, getLabRequestSamples, updateLabRequestSample } from "../../../store/labCitec/labRequestSamples/actions";
import { labRequestSampleStep } from "../../../shared/config/labRequestSampleSteps";
import { postApiCall } from "../../../services/mainServices";
import { labRequestSamples } from "../../../helpers/adipiBackendUrl";
import { toast } from "react-toastify";

export function useModalFormHook({
  mode = sampleMode.sample,
  labRequest = null,
  senderUser = null,
  id,
  modalClose,
  laboratoryType,
  pageSize,
}) {
  const [inheritedIsUpdate, setInheritedIsUpdate] = useState(false);
  const [inheritedIsClone, setInheritedIsClone] = useState(false);
  const [inheritedRowInfo, setInheritedRowInfo] = useState({});
  const [inheritedShowModal, setInheritedShowModal] = useState(false);
  const [sampleCount, setSampleCount] = useState(1);
  const [samples, setSamples] = useState([]);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [hasChanged, setHasChanged] = useState(false);

  const dispatch = useDispatch();

  const extractId = (value) => {
    return typeof value === "object" && value?.id ? value.id : value || "";
  };

  const handleSubmit = async (values) => {
    try {
      for (let index = 0; index < samples.length; index++) {
        const sample = samples[index];

        let submissionData = {
          ...values,
          name: sample.description || `Sample ${index + 1}`,
          dateAt: sample.date || moment().format("YYYY-MM-DD"),
          customer: extractId(values.customer) || null,
          senderUser: extractId(values.senderUser) || null,
          receiverUser: extractId(values.receiverUser) || null,
          location: extractId(values.location) || null,
          region: extractId(sample.region || values.region) || null,
          kind: extractId(sample.kind || values.kind) || null,
          unitMeasure: extractId(sample.unitMeasure || values.unitMeasure) || null,
          quantity: sample.quantity !== undefined && sample.quantity !== "" ? Number(sample.quantity) : values.quantity,
          laboratoryArea: extractId(values.laboratoryArea) || null,
          laboratorySegment: extractId(values.laboratorySegment) || null,
          coordination: extractId(values.coordination) || null,
        };

        // Set step to analysisRequest if cloning and in labRequest mode
        if (inheritedIsClone && mode === sampleMode.labRequest) {
          submissionData = {
            ...submissionData,
            step: labRequestSampleStep.analysisRequest,
          };
        }

        if (inheritedIsUpdate) {
          dispatch(updateLabRequestSample({ ...submissionData, id: inheritedRowInfo.id }));
          // Tiny delay between updates if needed
          await new Promise((resolve) => setTimeout(resolve, 300));
        } else {
          const newSampleData = mode === sampleMode.labRequest
            ? { ...submissionData, labRequest: id }
            : submissionData;

          // Call backend sequentially and wait for each sample insertion to complete
          // to avoid concurrent/overlapping sequence number checks.
          await postApiCall(labRequestSamples, newSampleData);
        }
      }

      toast.success(inheritedIsUpdate ? 'Registro actualizado correctamente' : 'Muestras registradas correctamente');

      // Refresh list
      if (mode === sampleMode.labRequest) {
        dispatch(getLabRequestSamples(`?limit=${pageSize}&page=0&labRequest=${id}`));
      } else {
        dispatch(getLabRequestSamples(`?limit=${pageSize}&page=0&step=${labRequestSampleStep.sampleControl}&laboratoryType=${laboratoryType}`));
      }
    } catch (error) {
      toast.error(inheritedIsUpdate ? 'Error al actualizar el registro' : 'Error al registrar las muestras');
    }
  };

  // Helper functions to validate the sample fields
  const dateNow = moment().format("YYYY-MM-DD");
  const validateSampleModeAndId = (id, inheritedId) => {
    if (mode === sampleMode.labRequest && id !== undefined) return id;
    if ((inheritedIsUpdate || inheritedIsClone) && inheritedId !== undefined) return inheritedId;
    return "";
  };
  const getSelectValue = (field) => (inheritedIsUpdate || inheritedIsClone) ? inheritedRowInfo?.[field]?.id ?? "" : "";
  const getFormattedDate = (date, fallback = dateNow) => date ? DateUtil.formatDisplay(date) : fallback;
  const getSafeValue = (value, fallback = "") => value ?? fallback;

  const formikFormData = useFormik({
    enableReinitialize: true,
    initialValues: {
      //*--- Date fields:
      dateAt: getFormattedDate(
        (inheritedIsUpdate || inheritedIsClone) ? inheritedRowInfo?.dateAt : "", ""),
      estimateReceivedAt: getFormattedDate(
        (inheritedIsUpdate || inheritedIsClone) ? inheritedRowInfo?.estimateReceivedAt : "",
        mode === sampleMode.labRequest ? "" : dateNow
      ),
      receivedAt: getFormattedDate(
        inheritedRowInfo?.receivedAt,
        mode === sampleMode.labRequest ? dateNow : ""
      ),
      wastedAt: getFormattedDate(
        inheritedRowInfo?.wastedAt,
        mode === sampleMode.labRequest ? dateNow : ""
      ),
      //*--- Other fields:
      quantity: getSafeValue(inheritedRowInfo?.quantity, 0),      // This is managed separately from the main form
      name: getSafeValue(inheritedRowInfo?.name),
      observations: getSafeValue(inheritedRowInfo?.observations),
      sampleType: mode === sampleMode.labRequest ? "laboratory" : "internal",
      evaluationFlag: "false",
      step: mode === sampleMode.labRequest
        ? (inheritedIsUpdate || inheritedIsClone) && inheritedRowInfo?.step
          ? inheritedRowInfo.step
          : labRequestSampleStep.analysisRequest
        : labRequestSampleStep.sampleControl,
      //*--- Select fields:
      laboratoryArea: validateSampleModeAndId(labRequest?.laboratoryArea?.id, inheritedRowInfo?.laboratoryArea?.id),
      laboratorySegment: validateSampleModeAndId(labRequest?.laboratorySegment?.id, inheritedRowInfo?.laboratorySegment?.id),
      coordination: validateSampleModeAndId(labRequest?.coordination?.id, inheritedRowInfo?.coordination?.id),
      customer: validateSampleModeAndId(labRequest?.customer?.id, inheritedRowInfo?.customer?.id),
      senderUser: validateSampleModeAndId(senderUser, inheritedRowInfo?.senderUser?.id),
      receiverUser: validateSampleModeAndId(labRequest?.technicalAdviser?.id, inheritedRowInfo?.receiverUser?.id),
      kind: getSelectValue("kind"),
      unitMeasure: getSelectValue("unitMeasure"),
      location: getSelectValue("location"),
      region: getSelectValue("region"),
    },
    validationSchema: Yup.object({
      //*--- Other fields:
      quantity: Yup.number().min(1, "Ingrese un número mayor a 0").required("Ingrese la cantidad"),
      observations: Yup.string().required("Ingrese las observaciones"),
      //*--- Select fields:
      laboratoryArea: Yup.string().required("Seleccione área de laboratorio"),
      laboratorySegment: Yup.string().required("Seleccione segmento de laboratorio"),
      coordination: Yup.string().required("Seleccione coordinación"),
      customer: Yup.string().required("Seleccione cliente"),
      senderUser: Yup.string().required("Seleccione remitente"),
      receiverUser: mode === sampleMode.labRequest
        ? Yup.string().nullable()
        : Yup.string().required("Seleccione usuario que recibe"),
      location: Yup.string().nullable().optional(),
      region: Yup.string().required("Seleccione región"),
      kind: Yup.string().required("Seleccione tipo de muestra"),
      unitMeasure: Yup.string().required("Seleccione unidad de medida"),
      //*--- Date fields:
      dateAt: Yup.string().required("Ingrese la fecha de muestreo"),
      wastedAt: Yup.string().required("Ingrese la fecha de desecho"),
      receivedAt: Yup.string().required("Ingrese la fecha de recepción"),
      estimateReceivedAt: mode === sampleMode.labRequest
        ? Yup.string().required("Ingrese la fecha estimada de recepción")
        : Yup.string(),
      //*--- Sample fields (name and dateAt) are managed apart from the main form (they are validated separately: see handleSave function)
    }),
    onSubmit: (values, { resetForm }) => {
      handleSubmit(values);
      modalClose();
      resetForm();
      setSampleCount(1);
      setSamples([{ description: "", date: moment().format("YYYY-MM-DD") }]);
      if (typeof values.resetActiveTab === "function") {
        values.resetActiveTab();
      }
      setInheritedShowModal(false);
      setIsButtonDisabled(false);
      setInheritedIsClone(false);
    },
  });

  const validateAndProceedToNextTab = async (setActiveTab) => {
    formikFormData.setTouched({
      dateAt: true,
      receivedAt: true,
      estimateReceivedAt: true,
      wastedAt: true,
      sampleCount: true,
      laboratoryArea: true,
      laboratorySegment: true,
      coordination: true,
      customer: true,
      senderUser: true,
      receiverUser: true,
      region: true,
      observations: true,
      quantity: true,
      kind: true,
      unitMeasure: true,
    }, true);
    // Explicitly validate the form and wait for validation results
    const errors = await formikFormData.validateForm();
    if (Object.keys(errors).length === 0) {
      setActiveTab("2");  // Only switch tabs if the form is valid
    }
  };

  // Effect to prefill samples data when editing an existing record
  useEffect(() => {
    const initialDate = formikFormData.values?.dateAt || moment().format("YYYY-MM-DD");
    const initialKind = formikFormData.values?.kind || "";
    const initialRegion = formikFormData.values?.region || "";
    const initialUnitMeasure = formikFormData.values?.unitMeasure || "";
    const initialQuantity = formikFormData.values?.quantity ?? "";
    setSamples(prev =>
      Array.from({ length: sampleCount }, (_, index) => ({
        description: prev[index]?.description || "",
        date: prev[index]?.date || initialDate,
        kind: prev[index]?.kind || initialKind,
        region: prev[index]?.region || initialRegion,
        unitMeasure: prev[index]?.unitMeasure || initialUnitMeasure,
        quantity: prev[index]?.quantity !== undefined && prev[index]?.quantity !== "" ? prev[index]?.quantity : initialQuantity,
      }))
    );
  }, [sampleCount, formikFormData.values?.dateAt]);

  // Detect user interaction with the form
  useEffect(() => {
    if (formikFormData.dirty) {
      setHasChanged(true);
    }
  }, [formikFormData.dirty]);

  useEffect(() => {
    if (!inheritedShowModal) {
      setHasChanged(false);
      setIsButtonDisabled(!(inheritedIsUpdate || inheritedIsClone));
    } else {
      setIsButtonDisabled(!((hasChanged || inheritedIsUpdate || inheritedIsClone) && formikFormData.isValid));
    }
  }, [hasChanged, formikFormData.isValid, inheritedShowModal, inheritedIsClone, inheritedIsUpdate]);

  const setInitialSampleDate = (date) => {
    const newDate = date || moment().format("YYYY-MM-DD");
    setSamples(prevSamples =>
      prevSamples.map(sample => ({ ...sample, date: newDate }))
    );
  };

  return {
    inheritedIsUpdate,
    setInheritedIsUpdate,
    inheritedIsClone,
    setInheritedIsClone,
    inheritedRowInfo,
    setInheritedRowInfo,
    inheritedShowModal,
    setInheritedShowModal,
    formikFormData,
    sampleCount,
    setSampleCount,
    samples,
    setSamples,
    validateAndProceedToNextTab,
    isButtonDisabled,
    setIsButtonDisabled,
    hasChanged,
    setInitialSampleDate,
  };
}